import {
  PARTNER_PERMISSIONS, PartnerMemberStatus, PartnerPermission, PermissionRequirement,
} from '../models/partner-permission.model';

const PERMISSION_PREFIX = 'PERMISSION_REQUIRED:';

/** Shown instead of an unlocked patient phone number to users without VIEW_CONTACT_INFO. */
export const HIDDEN_CONTACT_LABEL = 'Hidden (no permission)';

/** Unlocked phone number, or the hidden-contact label when the user lacks VIEW_CONTACT_INFO. */
export function displayPhone(lead?: { phoneNumber?: string; contactInfoRestricted?: boolean } | null): string | undefined {
  if (lead?.phoneNumber) return lead.phoneNumber;
  return lead?.contactInfoRestricted ? HIDDEN_CONTACT_LABEL : undefined;
}

/** Keeps only permissions whose prerequisite is also kept; canonical order; no duplicates. */
export function normalizePermissions(selected: PartnerPermission[]): PartnerPermission[] {
  const kept: PartnerPermission[] = [];
  for (const def of PARTNER_PERMISSIONS) {
    if (!selected.includes(def.code)) continue;
    if (def.requires && !kept.includes(def.requires)) continue;
    kept.push(def.code);
  }
  return kept;
}

export function isPermissionSelectable(code: PartnerPermission, selected: PartnerPermission[]): boolean {
  const def = PARTNER_PERMISSIONS.find(p => p.code === code);
  return !def?.requires || selected.includes(def.requires);
}

/**
 * Backend answers 403 {message: "PERMISSION_REQUIRED:<CODE>"} when a permission is missing.
 * `<CODE>` isn't restricted to today's known set on purpose: a code the FE doesn't know yet
 * (a permission added on the backend before the FE ships support for it) must still be
 * recognised as "a permission requirement" - see permissionDeniedMessage's default case - rather
 * than falling through to the generic 403 handling (which logs the user out).
 */
export function parsePermissionRequired(error: any): string | null {
  if (error?.status !== 403) return null;
  const message: unknown = error?.error?.message;
  if (typeof message !== 'string' || !message.startsWith(PERMISSION_PREFIX)) return null;
  const code = message.substring(PERMISSION_PREFIX.length);
  return code.length ? code : null;
}

export function permissionDeniedMessage(req: string): string {
  switch (req as PermissionRequirement) {
    case 'VIEW_HEALTH_DATA':
      return "Viewing patient health data requires the 'View patient health data' permission and your health data declaration. Contact the clinic owner.";
    case 'MANAGE_QUOTES':
      return "Creating quotes requires the 'Create & send quotes' permission. Contact the clinic owner.";
    case 'MANAGE_REQUESTS':
      return "You have read-only access. Accepting or rejecting requests requires the 'Accept / reject requests' permission. Contact the clinic owner.";
    case 'AUTHORIZE_PAYMENTS':
      return "Paid actions require the 'Authorize paid actions' permission. Contact the clinic owner.";
    case 'VIEW_CONTACT_INFO':
      return "Viewing patient contact info requires the 'View patient contact info' permission. Contact the clinic owner.";
    case 'OWNER':
      return 'Only the clinic owner can perform this action.';
    default:
      // Unknown code (see parsePermissionRequired's doc comment above).
      return "You don't have permission to perform this action. Contact the clinic owner.";
  }
}

/**
 * Every partner action that commits the clinic goes through ConfirmationModalComponent:
 * phone unlocks always pass requirePhoneDigits, acceptances always pass freeAcceptance.
 */
export function requiredPermissionForConfirmation(
  opts: { requirePhoneDigits?: boolean; freeAcceptance?: boolean; [k: string]: any },
): PartnerPermission | null {
  if (opts.requirePhoneDigits) return 'AUTHORIZE_PAYMENTS';
  if (opts.freeAcceptance === false) return 'AUTHORIZE_PAYMENTS';
  if (opts.freeAcceptance === true) return 'MANAGE_REQUESTS';
  return null;
}

export function memberStatusLabel(m: { status: PartnerMemberStatus; expired: boolean }): string {
  if (m.status === 'INVITED') return m.expired ? 'Expired' : 'Invited';
  return m.status === 'ACTIVE' ? 'Active' : 'Disabled';
}

export function permissionShortLabels(perms: PartnerPermission[]): string {
  const labels = PARTNER_PERMISSIONS.filter(p => perms.includes(p.code)).map(p => p.shortLabel);
  return labels.length ? labels.join(' · ') : 'Read-only';
}

/**
 * A PARTNER profile predates this feature (old backend, deployed before the FE) when it has no
 * `permissions` array at all. Such a partner must not be locked out by the new gating: treat them
 * as a legacy owner with unrestricted access instead.
 */
export function isLegacyPartnerProfile(profile?: { permissions?: PartnerPermission[] } | null): boolean {
  return !!profile && profile.permissions === undefined;
}
